//
// ============================================================================
// IMPORTS
// ============================================================================
//

import { useAuthClient } from "../../lib/auth-client";
import { SIGNALR_HUBS, SIGNALR_EVENTS } from "../../constants/signalRHubs";
import { SignalRClient } from "../../services/api/clients/SignalRClient";

//
// ============================================================================
// STATE (Module-Level Singleton)
// ============================================================================
//

/**
 * Singleton SignalRClient instance shared across all composable calls.
 * Ensures only one WebSocket connection per hub.
 */
let signalRClient: SignalRClient | null = null;

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * SignalR Real-Time Communication Composable
 *
 * Manages WebSocket connections to SignalR hubs for real-time server push notifications.
 * Provides centralized connection management with automatic reconnection and multi-hub support.
 *
 * Responsibilities:
 * - Create and manage singleton SignalRClient instance
 * - Connect/disconnect to multiple SignalR hubs
 * - Subscribe/unsubscribe to server events with type safety
 * - Track hub connection states reactively
 * - Handle automatic reconnection via SignalR's built-in retry logic
 * - Provide JWT token authentication for all connections
 * - Cleanup connections on logout
 *
 * Architecture:
 * - Singleton pattern: One SignalRClient per application
 * - Hub-specific state: Each hub has independent connection state
 * - State persistence: Connection states survive component unmounts and HMR
 * - Event handlers: UUID-based handler tracking for proper cleanup
 * - Auto-reconnection: SignalR handles reconnection automatically
 *
 * Supported Hubs:
 * - MASTER: Main application hub for general notifications
 * - Additional hubs can be added via SIGNALR_HUBS constant
 *
 * Use Cases:
 * - Real-time pending sample updates
 * - Server-side validation notifications
 * - System status changes
 * - Multi-user collaboration updates
 * - Live instrument status monitoring
 */
export const useSignalR = () => {
  //
  // ============================================================================
  // STATE
  // ============================================================================
  //
  const isInitializing = useState("signalr.initializing", () => false);
  const clientInitialized = useState("signalr.clientInitialized", () => false);
  const connectionError = useState<string | null>("signalr.error", () => null);

  //
  // ============================================================================
  // METHODS - CLIENT MANAGEMENT
  // ============================================================================
  //

  /**
   * Get hub-specific connection state
   *
   * Purpose:
   * Retrieve reactive connection state for a specific hub.
   * State persists across component lifecycles and HMR.
   *
   * Responsibilities:
   * - Create or retrieve hub-specific useState
   * - Initialize to false (disconnected)
   * - Return reactive boolean ref
   *
   * @param hubName - Hub identifier from SIGNALR_HUBS
   * @returns Ref<boolean> - Reactive connection state
   */
  const getHubState = (hubName: keyof typeof SIGNALR_HUBS) => {
    return useState<boolean>(`signalr.hub.${hubName}.connected`, () => false);
  };

  /**
   * Get or create SignalRClient singleton
   *
   * Purpose:
   * Provide access to singleton SignalRClient instance.
   * Creates client on first call, returns cached instance on subsequent calls.
   *
   * Responsibilities:
   * - Create SignalRClient if not exists
   * - Configure client with base URL and token factory
   * - Register state change callback (once)
   * - Return singleton instance
   *
   * @returns SignalRClient instance
   */
  const getClient = (): SignalRClient => {
    // Create client if not exists
    if (!signalRClient) {
      const { getToken } = useAuthClient();

      // apiUrlCombine in SignalRClient handles base URL automatically
      signalRClient = new SignalRClient(getToken);
    }

    // Register state change callback (only once)
    if (!clientInitialized.value) {
      signalRClient.setOnStateChange(() => {
        // Update ALL hub states when ANY connection changes
        Object.keys(SIGNALR_HUBS).forEach((hubName) => {
          const typedHubName = hubName as keyof typeof SIGNALR_HUBS;
          const hubState = getHubState(typedHubName);
          const isConnected = signalRClient!.isHubConnected(typedHubName);

          // Only log if state actually changed
          if (hubState.value !== isConnected) {
            console.log(`[SignalR] Hub ${typedHubName} → ${isConnected ? "connected" : "disconnected"}`);
            hubState.value = isConnected;
          }
        });
      });

      clientInitialized.value = true;
    }

    return signalRClient;
  };

  /**
   * Reset SignalR client and all hub states
   *
   * Purpose:
   * Complete cleanup of SignalR state for logout or error recovery.
   * Ensures clean slate for next user session.
   *
   * Responsibilities:
   * - Set client instance to null (force recreation on next use)
   * - Reset all hub connection states to false
   * - Clear initialization flag
   * - Clear error state
   *
   * Note: Does NOT disconnect hubs - call disconnect() first if needed
   */
  const resetClient = () => {
    signalRClient = null;

    // Reset all hub states
    Object.keys(SIGNALR_HUBS).forEach((hubName) => {
      const typedHubName = hubName as keyof typeof SIGNALR_HUBS;
      const hubState = getHubState(typedHubName);
      hubState.value = false;
    });

    // Reset flags
    isInitializing.value = false;
    clientInitialized.value = false;
    connectionError.value = null;
  };

  //
  // ============================================================================
  // METHODS - CONNECTION MANAGEMENT
  // ============================================================================
  //

  /**
   * Connect to one or more SignalR hubs
   *
   * Purpose:
   * Establish WebSocket connections to specified SignalR hubs.
   * Supports parallel connection to multiple hubs for efficiency.
   *
   * Responsibilities:
   * - Prevent concurrent connection attempts (guard with isInitializing)
   * - Get or create SignalRClient singleton
   * - Connect to all specified hubs in parallel
   * - Update hub states immediately after connection
   * - Handle connection errors gracefully (don't crash app)
   * - Clear error state on new connection attempt
   *
   * Connection Flow:
   * 1. Check isInitializing guard (prevent race conditions)
   * 2. Set isInitializing and clear errors
   * 3. Get SignalRClient instance
   * 4. Create connections in parallel with Promise.all
   * 5. Update hub states for all connected hubs
   * 6. State change callback handles future reconnections automatically
   *
   * Automatic Reconnection:
   * SignalR handles reconnection automatically after initial connection.
   * State change callback updates reactive states on reconnect.
   *
   * @param hubNames - Array of hub identifiers to connect to (default: ["MASTER"])
   * @returns Promise that resolves when all hubs are connected
   */
  const connect = async (hubNames: Array<keyof typeof SIGNALR_HUBS> = ["MASTER"]): Promise<void> => {
    // Prevent concurrent connections
    if (isInitializing.value) {
      console.warn("⚠️  SignalR connection already in progress");
      return;
    }

    try {
      isInitializing.value = true;
      connectionError.value = null;

      const client = getClient();

      // Connect to all hubs in parallel for better performance
      const connectionPromises = hubNames.map((hubName) => {
        const endpoint = SIGNALR_HUBS[hubName];
        return client.createConnection(hubName, endpoint);
      });

      await Promise.all(connectionPromises);

      // Update hub states immediately after connection
      // (Callback will handle reconnections)
      hubNames.forEach((hubName) => {
        const hubState = getHubState(hubName);
        const isConnected = client.isHubConnected(hubName);
        hubState.value = isConnected;
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      connectionError.value = errorMessage;

      // Set all hub states to false on error
      hubNames.forEach((hubName) => {
        const hubState = getHubState(hubName);
        hubState.value = false;
      });
      // Don't throw - allow app to continue without SignalR
    } finally {
      isInitializing.value = false;
    }
  };

  /**
   * Disconnect all SignalR hubs and reset client
   *
   * Purpose:
   * Cleanly close all WebSocket connections and cleanup state.
   * Called on user logout to ensure proper disconnection.
   *
   * Responsibilities:
   * - Disconnect all hubs via client.disconnectAll()
   * - Reset client and all states
   * - Handle disconnection errors
   *
   * Note: Throws error if disconnection fails (caller should handle)
   */
  const disconnect = async (): Promise<void> => {
    if (!signalRClient) {
      console.warn("ℹ️  No SignalR client to disconnect");
      return;
    }

    try {
      await signalRClient.disconnectAll();
      resetClient();
    } catch (error) {
      console.error("❌ SignalR disconnect failed:", error);
      throw error;
    }
  };

  //
  // ============================================================================
  // METHODS - EVENT SUBSCRIPTION
  // ============================================================================
  //

  /**
   * Subscribe to a SignalR event
   *
   * Purpose:
   * Register callback function to receive server-sent events from specific hub.
   * Automatically connects to hub if not already connected.
   *
   * Responsibilities:
   * - Get SignalRClient instance
   * - Check if hub connection exists
   * - Auto-connect if hub not connected
   * - Register event handler with client
   * - Return cleanup function for easy unsubscribe
   *
   * @param eventName - Event name (e.g., "MESSAGES_LOGGED")
   * @param callback - Callback function to handle event data
   * @param hubName - Hub identifier (required)
   * @returns Cleanup function to unsubscribe
   */
  const subscribe = async <T = any>(
    eventName: string,
    callback: (data: T) => void,
    hubName: keyof typeof SIGNALR_HUBS,
  ): Promise<() => void> => {
    const client = getClient();
    let connection = await client.getConnection(hubName);

    // Auto-connect if not connected
    if (!connection) {
      await connect([hubName]);
      connection = await client.getConnection(hubName);
    }

    if (!connection) {
      throw new Error(`Failed to get connection for ${hubName}`);
    }

    // Subscribe and get handler ID
    const handlerId = client.on(hubName, eventName, callback);

    // Return cleanup function
    return () => {
      const removed = client.off(hubName, eventName, handlerId);
      if (removed) {
        console.log(`[SignalR] Unsubscribed from ${hubName}.${eventName} [${handlerId}]`);
      }
    };
  };

  /**
   * Unsubscribe from a SignalR event using handler ID
   *
   * Purpose:
   * Remove specific event handler by its unique ID.
   * Alternative to using cleanup function from subscribe().
   *
   * Responsibilities:
   * - Get SignalRClient instance
   * - Remove handler by ID from event handlers map
   * - Log successful unsubscription
   *
   * Note: Prefer using cleanup function from subscribe() for simpler code.
   * Use this method when you need to store and manage handler IDs manually.
   *
   * @param eventName - Event name
   * @param hubName - Hub identifier
   * @param handlerId - Handler ID from subscribe
   */
  const unsubscribe = (eventName: string, hubName: keyof typeof SIGNALR_HUBS, handlerId: string): void => {
    const client = getClient();
    const removed = client.off(hubName, eventName, handlerId);

    if (removed) {
      console.log(`[SignalR] Unsubscribed from ${hubName}.${eventName} [${handlerId}]`);
    }
  };

  /**
   * Check if a specific hub is currently connected
   *
   * Purpose:
   * Quickly check connection status of a specific hub.
   * Useful for conditional logic and UI state.
   *
   * Responsibilities:
   * - Check if client exists
   * - Query hub connection state from client
   * - Return false if client not initialized
   *
   * Note: For reactive connection state, use getHubState() instead.
   * This is a one-time check, not reactive.
   *
   * @param hubName - Hub identifier
   * @returns True if connected, false otherwise
   */
  const isHubConnected = (hubName: keyof typeof SIGNALR_HUBS): boolean => {
    return signalRClient?.isHubConnected(hubName) ?? false;
  };

  return {
    // State
    isInitializing: readonly(isInitializing),
    connectionError: readonly(connectionError),

    // Methods
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    isHubConnected,
    getHubState,
    getClient,
    resetClient,

    // Constants
    EVENTS: SIGNALR_EVENTS,
  };
};
